import { signIn } from "aws-amplify/auth";
import type { PortalAuthClientWithSelfUI } from "@/lib/portalAuthClient";
import {
  clearRememberMeFromStorage,
  setRememberMeToStorage,
  setLastLoginId,
} from "@/utils/storage";
import { AUTH_LOGIN_OPTIONS } from "@/utils/const";
import type {
  LoginOptionsRequest,
  LoginOptionsResponse,
} from "@shared-domain/auth-service/contract";
import { validatePasswordRequirements } from "@/utils/passwordValidation";
import { translateCognitoMessage } from "@/utils/cognitoErrorMessages";
import { isPlatformAuthenticatorAvailable } from "@/utils/webauthnSupport";
import type { useLoginState } from "./useLoginState";

/**
 * PRODUCT_DEV-396 ログイン画面で login-options API (認証不要) を呼び出してパスキー利用可否を取得
 * 失敗時は false 扱い（フォールバックでパスワード入力）
 */
async function fetchPasskeyAvailable(email: string): Promise<boolean> {
  try {
    const body: LoginOptionsRequest = { email };
    const res = await fetch(AUTH_LOGIN_OPTIONS, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) return false;
    const data = (await res.json()) as LoginOptionsResponse;
    return Boolean(data?.passkeyEnabled);
  } catch (err) {
    console.warn("login-options fetch failed:", err);
    return false;
  }
}

/**
 * 認証エラーのハンドリング共通ユーティリティ
 * Cognito/Amplify のエラーオブジェクトから message と recoverySuggestion を抽出して state にセットする
 */
export const handleAuthError = (
  err: unknown,
  state: { setError: (s: string) => void; setRecoverySuggestion: (s: string | null) => void },
  fallbackMessage: string,
) => {
  console.error(err);
  const error = err as { message?: string; recoverySuggestion?: string };
  const translated = translateCognitoMessage(error.message);
  if (translated) {
    state.setError(translated);
    state.setRecoverySuggestion(null);
    return;
  }
  state.setError(error.message ?? fallbackMessage);
  state.setRecoverySuggestion(error.recoverySuggestion ?? null);
};

/**
 * PRODUCT_DEV-396 パスキー登録促進モーダルを表示すべきか判定
 * テスト用: forcePasskeyPrompt="1"=常に表示 / "0"=常に非表示
 *
 * 通常ロジック (UA単位デバイス管理に変更後):
 *   - テナント未許可 → false
 *   - このブラウザで登録済み (currentDeviceHasPasskey === true) → false
 *   - このブラウザ未登録 → 最終促進から30日経過なら true (初回も true)
 *
 * 別端末で登録済 (currentDeviceHasPasskey===false かつ Cognito 側にあり) でも、
 * このブラウザに対しては促進する → 「このブラウザでも登録しよう」を促せる。
 */
export function shouldShowPasskeyPrompt(args: {
  client: PortalAuthClientWithSelfUI;
  forcePrompt: string | null;
  lastPasskeyPromptedAt: string | undefined;
}): boolean {
  const { client, forcePrompt, lastPasskeyPromptedAt } = args;

  // テスト用クエリオーバーライド
  if (forcePrompt === "1") return true;
  if (forcePrompt === "0") return false;

  if (!client.me) return false;
  const memberships = client.me.memberships;
  const anyTenantAllowsPasskey = memberships.some(
    (m) => m.tenantPasskeyEnabled,
  );
  if (!anyTenantAllowsPasskey) return false;

  // このブラウザで既に登録済みなら促進不要
  if (client.me.currentDeviceHasPasskey === true) return false;

  // 30日経過判定 (初回は表示)
  if (!lastPasskeyPromptedAt) return true;
  const promptedAt = new Date(lastPasskeyPromptedAt).getTime();
  if (!Number.isFinite(promptedAt)) return true;
  const daysSince = (Date.now() - promptedAt) / 86400000;
  return daysSince >= 30;
}

/**
 * ログインページの全てのハンドラー関数を集約したカスタムフック
 */
export const useLoginHandlers = (
  client: PortalAuthClientWithSelfUI,
  state: ReturnType<typeof useLoginState>,
  tenantIdFromQuery: string | null,
  productIdFromQuery: string | null,
  _mfaFromQuery: string | null,
  forcePasskeyPromptFromQuery: string | null = null,
  // PRODUCT_DEV-421 alias ログインモード時の tenantId (URL `/:tenantId` or `?tenant=` 由来)
  aliasTenantId: string | null = null,
) => {
  /**
   * ユーザ情報の取得～テナント選択画面またはリダイレクト
   */
  const whoamiAndCheckProduct = async (options?: { skipPasskeySetup?: boolean }) => {
    state.setLoadingMessage("ユーザ情報取得中...");

    // /me 呼び出し
    await client.whoami();

    if (!client.me) {
      throw new Error("ユーザ情報の取得に失敗しました。");
    }

    // PRODUCT_DEV-396 パスキー登録促進モーダル
    if (
      !state.showPasskeyPromptModal &&
      !options?.skipPasskeySetup &&
      !state.passkeyLoginCancelled
    ) {
      const shouldPrompt = shouldShowPasskeyPrompt({
        client,
        forcePrompt: forcePasskeyPromptFromQuery,
        lastPasskeyPromptedAt: client.me.lastPasskeyPromptedAt,
      });
      if (shouldPrompt) {
        // Platform Authenticator (Windows Hello / Touch ID 等) が無いと
        // associateWebAuthnCredential() が QR コードでクロスデバイス認証を出すため、
        // 事前判定で利用不可ならモーダル自体を表示しない。
        // lastPasskeyPromptedAt は更新せず、端末追加で利用可能になったら次回表示する。
        const platformAvailable = await isPlatformAuthenticatorAvailable();
        if (platformAvailable) {
          state.setShowPasskeyPromptModal(true);
          state.setLoading(false);
          state.setLoadingMessage("");
          return;
        }
      }
    }

    state.setLoadingMessage("サービス情報取得中...");

    // テナント一覧を構築
    const memberships = client.me.memberships;

    // クエリパラメータでテナントが指定されている場合はフィルタ
    const filteredMemberships = tenantIdFromQuery
      ? memberships.filter((m) => m.tenantId === tenantIdFromQuery)
      : memberships;

    if (filteredMemberships.length === 0) {
      throw new Error("利用可能なテナントがありません。");
    }

    const tenantList = filteredMemberships
      .map((m) => ({
        tenantId: m.tenantId,
        tenantName: m.tenantName,
        role: m.role,
        mfaRequired: m.mfaRequired,
        products: m.products,
      }))
      .sort((a, b) => a.tenantName.localeCompare(b.tenantName, "ja"));

    if (tenantList.length === 1) {
      // テナント1件 → 自動選択
      await handleTenantChoice(tenantList[0]);
    } else {
      // 複数テナント → 選択画面表示
      state.setTenants(tenantList);
      state.setShowTenantSelect(true);
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * テナント選択後の処理: MFAチェック → プロダクト選択
   */
  const handleTenantChoice = async (
    tenant: import("../types").LoginPageTenant,
  ) => {
    state.setSelectedTenant(tenant);
    state.setShowTenantSelect(false);
    state.setLoading(true);
    state.setLoadingMessage("テナント情報を確認中...");

    // MFA チェック: テナントの mfaRequired が true の場合、TOTP 未設定なら Setup へ
    if (tenant.mfaRequired) {
      const { isTOTPEnabled } = await client.checkMFAPreference();
      if (!isTOTPEnabled) {
        // TOTP 未設定 → セットアップ画面へ
        const { totpUri, secretKey } = await client.setupTOTP();
        state.setTotpUri(totpUri);
        state.setTotpSecretKey(secretKey);
        state.setShowTOTPSetup(true);
        state.setLoading(false);
        state.setLoadingMessage("");
        return;
      }
    }

    // プロダクト一覧を構築（選択テナント内のみ）
    await showProductsForTenant(tenant);
  };

  /**
   * テナント内のプロダクト一覧表示
   */
  const showProductsForTenant = async (
    tenant: import("../types").LoginPageTenant,
  ) => {
    // クエリパラメータでプロダクトが指定されている場合はフィルタ
    let products = tenant.products;
    if (productIdFromQuery) {
      products = products.filter((p) => p.id === productIdFromQuery);
      if (products.length === 0) {
        throw new Error("許可されていないプロダクトです。");
      }
    }

    const availableProducts = products
      .map((p) => ({
        ...p,
        tenantId: tenant.tenantId,
        tenantName: tenant.tenantName,
        isLoading: false,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, "ja"));

    if (availableProducts.length === 0) {
      throw new Error("利用中のプロダクトはありません。");
    } else if (availableProducts.length === 1) {
      // プロダクト1件 → 自動選択して即リダイレクト
      client.setAutoRedirect(true);
      const { id: productId, url, tenantId } = availableProducts[0];
      await handleProductChoice({ productId, url, tenantId });
    } else {
      // 複数プロダクト → 選択UI表示
      state.setProducts(availableProducts);
      state.setShowSelectUI(true);
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * プロダクトを1つ選んだあとの共通処理
   * - One-time code 発行
   * - リダイレクト
   */
  const handleProductChoice = async (props: {
    url: string;
    productId: string;
    tenantId: string;
  }) => {
    await client.issueOneTimeCode(props.productId, props.tenantId);
    await client.redirectProductUrl(props.url);
  };

  /**
   * ログインボタン押下時の処理
   * - ID/PWでのサインイン
   * - ログインユーザ情報の取得
   * - 遷移先プロダクトの特定
   * - 遷移先が1つの場合、プロダクトURLにリダイレクト
   */
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    state.setError(null);
    state.setRecoverySuggestion(null);
    state.setLoading(true);

    try {
      // サインイン
      state.setLoadingMessage("ログイン中...");
      // PRODUCT_DEV-421: alias モード時は resolve-alias 経由
      if (aliasTenantId) {
        await client.signInByAlias(aliasTenantId, state.email, state.password);
      } else {
        await client.signIn(state.email, state.password);
      }

      // チャレンジが必要な場合はチャレンジUIを表示
      if (client.challengeType) {
        state.setChallengeType(client.challengeType);

        // RESET_PASSWORDチャレンジの場合、リセットコード入力画面用にメールアドレスを設定
        if (client.challengeType === "RESET_PASSWORD") {
          state.setResetEmail(state.email);
        }

        state.setLoading(false);
        state.setLoadingMessage("");
        return;
      }

      // 「次回から自動的にログイン」の場合はストレージに保存
      setRememberMeToStorage(state.rememberMe);
      // パスキーログイン用にログインIDを保存
      setLastLoginId(state.email);

      await whoamiAndCheckProduct();
    } catch (err) {
      handleAuthError(err, state, "ログインに失敗しました");
      // PRODUCT_DEV-549: ログイン失敗で signOut(revoke) すると、既存の有効な共有 refreshToken が
      // 失効し他 product が落ちるため呼ばない。
      clearRememberMeFromStorage();
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * 新しいパスワード設定の処理
   */
  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    state.setError(null);
    state.setRecoverySuggestion(null);

    // バリデーション
    if (state.newPassword !== state.confirmPassword) {
      state.setError("パスワードが一致しません。");
      return;
    }

    const newPasswordError = validatePasswordRequirements(state.newPassword);
    if (newPasswordError) {
      state.setError(newPasswordError);
      return;
    }

    state.setLoading(true);
    state.setLoadingMessage("パスワード設定中...");

    try {
      await client.confirmChallenge(state.newPassword);

      // チャレンジが完了した場合
      if (!client.challengeType) {
        // 🔥 重要: 状態をクリアして画面遷移を可能にする
        state.setChallengeType(null);

        // 「次回から自動的にログイン」の場合はストレージに保存
        setRememberMeToStorage(state.rememberMe);
        // パスキーログイン用にログインIDを保存
        setLastLoginId(state.email);

        await whoamiAndCheckProduct();
      } else {
        // さらにチャレンジが必要な場合
        state.setChallengeType(client.challengeType);
      }
    } catch (err) {
      handleAuthError(err, state, "パスワード設定に失敗しました");
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * MFAコード確認の処理
   */
  const handleMfaCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    state.setError(null);
    state.setRecoverySuggestion(null);

    // バリデーション
    if (state.mfaCode.length !== 6) {
      state.setError("6桁のコードを入力してください。");
      return;
    }

    state.setLoading(true);
    state.setLoadingMessage("確認中...");

    try {
      await client.confirmChallenge(state.mfaCode);

      // チャレンジが完了した場合
      if (!client.challengeType) {
        // 🔥 重要: 状態をクリアして画面遷移を可能にする
        state.setChallengeType(null);

        // 「次回から自動的にログイン」の場合はストレージに保存
        setRememberMeToStorage(state.rememberMe);
        // パスキーログイン用にログインIDを保存
        setLastLoginId(state.email);

        await whoamiAndCheckProduct();
      } else {
        // さらにチャレンジが必要な場合
        state.setChallengeType(client.challengeType);
      }
    } catch (err) {
      handleAuthError(err, state, "認証コードの確認に失敗しました");
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * メールコード確認の処理
   */
  const handleEmailCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    state.setError(null);
    state.setRecoverySuggestion(null);

    // バリデーション
    if (state.emailCode.length !== 6) {
      state.setError("6桁のコードを入力してください。");
      return;
    }

    state.setLoading(true);
    state.setLoadingMessage("確認中...");

    try {
      await client.confirmChallenge(state.emailCode);

      // チャレンジが完了した場合
      if (!client.challengeType) {
        state.setChallengeType(null);
        setRememberMeToStorage(state.rememberMe);
        // パスキーログイン用にログインIDを保存
        setLastLoginId(state.email);
        await whoamiAndCheckProduct();
      } else {
        // さらにチャレンジが必要な場合
        state.setChallengeType(client.challengeType);
      }
    } catch (err) {
      handleAuthError(err, state, "認証コードの確認に失敗しました");
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * チャレンジキャンセルの処理
   */
  const handleChallengeCancel = async () => {
    state.setChallengeType(null);
    state.setNewPassword("");
    state.setConfirmPassword("");
    state.setMfaCode("");
    state.setError(null);
    state.setRecoverySuggestion(null);
    await client.signOut({ reason: "challenge-cancel" });
    clearRememberMeFromStorage();
  };

  /**
   * パスワードリセットの開始
   */
  const handleForgotPassword = () => {
    state.setResetFlowStep("email");
    state.setError(null);
    state.setRecoverySuggestion(null);
  };

  /**
   * リセットコード送信の処理
   */
  const handleSendResetCode = async (e: React.FormEvent) => {
    e.preventDefault();
    state.setError(null);
    state.setRecoverySuggestion(null);

    // バリデーション
    if (!state.resetEmail) {
      state.setError("メールアドレスを入力してください。");
      return;
    }

    state.setLoading(true);
    state.setLoadingMessage("リセットコードを送信中...");

    try {
      await client.resetPassword(state.resetEmail);
      state.setResetFlowStep("code");
      state.setError(null);
    } catch (err) {
      handleAuthError(err, state, "リセットコードの送信に失敗しました");
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * パスワードリセット完了の処理
   */
  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    state.setError(null);
    state.setRecoverySuggestion(null);

    // バリデーション
    if (state.resetCode.length !== 6) {
      state.setError("6桁のコードを入力してください。");
      return;
    }

    if (state.resetPassword !== state.resetConfirmPassword) {
      state.setError("パスワードが一致しません。");
      return;
    }

    const resetPasswordError = validatePasswordRequirements(state.resetPassword);
    if (resetPasswordError) {
      state.setError(resetPasswordError);
      return;
    }

    state.setLoading(true);
    state.setLoadingMessage("パスワードをリセット中...");

    try {
      await client.confirmResetPassword(
        state.resetEmail,
        state.resetCode,
        state.resetPassword
      );

      state.setEmail(state.resetEmail);
      state.setResetFlowStep(null);
      state.setResetEmail("");
      state.setResetCode("");
      state.setResetPassword("");
      state.setResetConfirmPassword("");
      state.setError(null);
      state.setRecoverySuggestion(
        "パスワードがリセットされました。新しいパスワードでログインしてください。",
      );
    } catch (err) {
      handleAuthError(err, state, "パスワードリセットに失敗しました");
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * TOTP セットアップの完了処理
   */
  const handleTOTPSetupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    state.setError(null);
    state.setRecoverySuggestion(null);

    if (state.totpVerifyCode.length !== 6) {
      state.setError("6桁のコードを入力してください。");
      return;
    }

    state.setLoading(true);
    state.setLoadingMessage("設定中...");

    try {
      await client.verifyAndEnableTOTP(state.totpVerifyCode);

      // 設定完了 → サインアウトして再ログインを要求
      state.setShowTOTPSetup(false);
      state.setTotpUri(null);
      state.setTotpSecretKey(null);
      state.setTotpVerifyCode("");
      await client.signOut({ reason: "totp-complete" });
      clearRememberMeFromStorage();
      state.setRecoverySuggestion("二段階認証の設定が完了しました。再度ログインしてください。");
    } catch (err) {
      handleAuthError(err, state, "認証コードの検証に失敗しました");
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * TOTP セットアップのキャンセル処理
   */
  const handleTOTPSetupCancel = async () => {
    state.setShowTOTPSetup(false);
    state.setTotpUri(null);
    state.setTotpSecretKey(null);
    state.setTotpVerifyCode("");
    state.setError(null);
    state.setRecoverySuggestion(null);
    await client.signOut({ reason: "totp-cancel" });
    clearRememberMeFromStorage();
  };

  /**
   * パスキーでログインする処理
   * @param emailOverride - state.emailの代わりに使用するメールアドレス（自動試行用）
   */
  const handlePasskeyLogin = async (emailOverride?: string) => {
    state.setError(null);
    state.setRecoverySuggestion(null);

    const email = emailOverride || state.email;
    if (!email) {
      state.setError("ログインIDを入力してからパスキーでログインしてください。");
      return;
    }

    state.setLoading(true);
    state.setLoadingMessage("パスキー認証中...");

    try {
      const result = await signIn({
        username: email,
        options: {
          authFlowType: "USER_AUTH",
          preferredChallenge: "WEB_AUTHN",
        },
      });

      if (result.isSignedIn) {
        // 成功: トークンを取得・保存してからプロダクト選択へ
        await client.handleSuccessfulSignIn(" (passkey)");
        setRememberMeToStorage(state.rememberMe);
        setLastLoginId(email);
        await whoamiAndCheckProduct({ skipPasskeySetup: true });
      } else if (result.nextStep) {
        // チャレンジが必要な場合
        state.setChallengeType(result.nextStep.signInStep);
      }
    } catch (err) {
      console.log("Passkey login cancelled or failed:", err);
      const errorName = (err as { name?: string })?.name;
      // NotAllowedError はユーザーキャンセル → フラグを立てて静かにフォールバック
      // それ以外（パスキー未登録など）→ ID/PWログイン後にパスキー登録を促す
      if (errorName === "NotAllowedError") {
        state.setPasskeyLoginCancelled(true);
      } else {
        state.setRecoverySuggestion(
          "パスキーが登録されていません。ID/パスワードでログイン後、パスキーを登録できます。",
        );
      }
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * PRODUCT_DEV-396 ログインID入力欄からフォーカスが外れた時 / autofill 検知時にパスキー利用可否を判定
   * login-options API (認証不要) を呼び出して passkeyAvailable を更新し、利用可なら自動的にパスキー認証を開始する。
   * ユーザがWebAuthnダイアログをキャンセルしたら handlePasskeyLogin 側で passkeyLoginCancelled が立ち、
   * 以降は ID/PW フォーム経由のログインに自然にフォールバックする。
   */
  const handleEmailBlur = async (email: string) => {
    const trimmed = email.trim();
    if (!trimmed || trimmed.length < 3) {
      state.setPasskeyAvailable(null);
      return;
    }

    // 自動トリガー可否の共通ガード
    // ローディング中・チャレンジ進行中・モーダル表示中・既ログイン中は起動しない
    // (既ログイン中に signIn を再実行すると テナント選択画面で無限ループに陥るため
    //  client.me ガード必須)
    const canAutoTrigger = () =>
      !state.passkeyLoginCancelled &&
      !state.loading &&
      !state.challengeType &&
      !state.showPasskeyPromptModal &&
      !client.me;

    // PRODUCT_DEV-484 alias モード: まず alias 解決を試みる。
    // 解決できれば cognitoUsername でパスキー判定/自動起動。
    // 解決できなければ（= 通常ログインIDを入力）下の email ベース判定にフォールスルー。
    if (aliasTenantId) {
      try {
        const { resolved, cognitoUsername, passkeyEnabled } =
          await client.resolveAlias(aliasTenantId, trimmed);
        if (resolved) {
          state.setPasskeyAvailable(passkeyEnabled);
          if (passkeyEnabled && canAutoTrigger()) {
            await handlePasskeyLogin(cognitoUsername);
          }
          return;
        }
        // alias 未解決 → 通常ログインID(email) 扱いでフォールスルー
      } catch (err) {
        console.warn("resolveAlias failed:", err);
        // フォールスルー
      }
    }

    const available = await fetchPasskeyAvailable(trimmed);
    state.setPasskeyAvailable(available);
    if (available && canAutoTrigger()) {
      await handlePasskeyLogin(trimmed);
    }
  };

  /**
   * PRODUCT_DEV-396 促進モーダルで「あとで」を選択した場合
   * lastPasskeyPromptedAt を現在時刻で更新し、モーダルを閉じてフローを継続
   */
  const handlePasskeyPromptLater = async () => {
    state.setShowPasskeyPromptModal(false);
    const tenantId = client.me?.currentTenantId ?? client.me?.memberships[0]?.tenantId ?? "";
    if (tenantId) {
      try {
        const { apiPatch } = await import("@shared-ui/utils/api");
        const { AUTH_ME } = await import("@/utils/const");
        await apiPatch(AUTH_ME, {
          tenantId,
          lastPasskeyPromptedAt: new Date().toISOString(),
        });
      } catch (err) {
        console.warn("Failed to update lastPasskeyPromptedAt:", err);
      }
    }
    // モーダルを閉じてフロー継続
    state.setLoading(true);
    try {
      await whoamiAndCheckProduct({ skipPasskeySetup: true });
    } catch (err) {
      handleAuthError(err, state, "処理に失敗しました");
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * PRODUCT_DEV-396 促進モーダルで「設定する」を選択した場合
   * モーダル内で直接 WebAuthn 登録ダイアログを起動 (1クリック化)。
   * 成功 → モーダル閉じてプロダクト選択へ進む。
   * キャンセル/失敗 → 「あとで」と同等に lastPasskeyPromptedAt を更新してフロー継続。
   */
  const handlePasskeyPromptSetup = async () => {
    state.setError(null);
    state.setRecoverySuggestion(null);
    state.setLoading(true);
    state.setLoadingMessage("パスキーを登録中...");
    try {
      await client.registerPasskey();
      state.setShowPasskeyPromptModal(false);
      state.setLoadingMessage("サービス情報取得中...");
      await whoamiAndCheckProduct({ skipPasskeySetup: true });
    } catch (err) {
      console.warn("Passkey registration cancelled or failed:", err);
      // キャンセル/失敗は「あとで」扱いで30日抑止 + フロー継続
      await handlePasskeyPromptLater();
    } finally {
      state.setLoading(false);
      state.setLoadingMessage("");
    }
  };

  /**
   * パスワードリセット画面の戻る処理
   */
  const handleResetBack = () => {
    state.setResetFlowStep("email");
    state.setResetCode("");
    state.setResetPassword("");
    state.setResetConfirmPassword("");
    state.setError(null);
    state.setRecoverySuggestion(null);
  };

  /**
   * パスワードリセットのキャンセル処理
   */
  const handleResetCancel = () => {
    state.setResetFlowStep(null);
    state.setResetEmail("");
    state.setResetCode("");
    state.setResetPassword("");
    state.setResetConfirmPassword("");
    state.setError(null);
    state.setRecoverySuggestion(null);
  };

  return {
    whoamiAndCheckProduct,
    handleTenantChoice,
    handleProductChoice,
    handleLogin,
    handleNewPasswordSubmit,
    handleMfaCodeSubmit,
    handleEmailCodeSubmit,
    handleChallengeCancel,
    handleTOTPSetupSubmit,
    handleTOTPSetupCancel,
    handlePasskeyLogin,
    handleForgotPassword,
    handleSendResetCode,
    handleResetPasswordSubmit,
    handleResetBack,
    handleResetCancel,
    // PRODUCT_DEV-396
    handleEmailBlur,
    handlePasskeyPromptLater,
    handlePasskeyPromptSetup,
  };
};
